"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useIdentity } from "@/lib/api/session";

const FAVORITES_KEY = "northstar:favorites";
const RECENT_KEY = "northstar:recent";
const RECENT_MAX = 24;

type Player = {
  favorites: string[];
  recent: string[];
  enabled: boolean;
  ready: boolean;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  recordPlay: (id: string) => void;
  clearRecent: () => void;
};

const PlayerContext = createContext<Player>({
  favorites: [],
  recent: [],
  enabled: false,
  ready: false,
  isFavorite: () => false,
  toggleFavorite: () => {},
  recordPlay: () => {},
  clearRecent: () => {},
});

export const usePlayer = () => useContext(PlayerContext);

function readList(key: string): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, value: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    return;
  }
}

export function PlayerProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useIdentity();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isSignedIn) {
      setReady(false);
      setFavorites([]);
      setRecent([]);
      return;
    }

    setFavorites(readList(FAVORITES_KEY));
    setRecent(readList(RECENT_KEY));
    setReady(true);
  }, [isSignedIn]);

  /* Updaters stay pure and persistence happens here, after hydration. Writing
     from inside a state updater raced the mount read above: React defers the
     updater, so the read could land after the write was queued and before it
     ran, leaving the list empty. */
  useEffect(() => {
    if (ready) writeList(FAVORITES_KEY, favorites);
  }, [ready, favorites]);

  useEffect(() => {
    if (ready) writeList(RECENT_KEY, recent);
  }, [ready, recent]);

  const toggleFavorite = useCallback(
    (id: string) => {
      if (!isSignedIn) return;
      setFavorites((current) =>
        current.includes(id) ? current.filter((v) => v !== id) : [id, ...current],
      );
    },
    [isSignedIn],
  );

  const recordPlay = useCallback(
    (id: string) => {
      if (!isSignedIn) return;
      setRecent((current) => [id, ...current.filter((v) => v !== id)].slice(0, RECENT_MAX));
    },
    [isSignedIn],
  );

  const clearRecent = useCallback(() => {
    if (!isSignedIn) return;
    setRecent([]);
  }, [isSignedIn]);

  const value = useMemo<Player>(
    () => ({
      favorites,
      recent,
      enabled: isSignedIn,
      ready,
      isFavorite: (id: string) => favorites.includes(id),
      toggleFavorite,
      recordPlay,
      clearRecent,
    }),
    [favorites, recent, isSignedIn, ready, toggleFavorite, recordPlay, clearRecent],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}
